import React, {CSSProperties, ReactNode} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {AnimatePresence, motion, MotionProps} from 'motion/react'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import minus from '../../../utils/minus'
import {Checkbox, CheckboxIcon} from '../Checkbox'
import {Radio, RadioIcon} from '../Radio'
import {Tag} from '../Tag'
import {Text} from '../Text'

const BOX_SHADOW_WIDTH = 1

export type ControlType = 'radio' | 'checkbox'

interface Props {
  selected: boolean
  dataId?: string
  name: string
  onChange: () => void
  value?: string
  children?: ReactNode
  labelText?: string
  withControlButton?: boolean
  withBorder?: boolean
  disabled?: boolean
  className?: string
  variant: ControlType
  style?: CSSProperties
  prefix?: ReactNode
  title?: ReactNode
  isVertical?: boolean
  hasCheckmarkError?: boolean
  hideDefaultControlButton?: boolean
  motionWrapperProps?: Pick<
    MotionProps,
    'initial' | 'animate' | 'exit' | 'transition' | 'variants'
  > & {
    key?: string
  }
  expandableContent?: ReactNode
}

const activeStyles = `
  border-color: ${cssTheme.colors.selectionCard.selected.border};
  box-shadow: inset 0 0 0 ${BOX_SHADOW_WIDTH}px
    ${cssTheme.colors.selectionCard.selected.border};
  background-color: ${cssTheme.colors.selectionCard.selected.background};
`

const disabledStyles = `
  border: 1px solid transparent;
  background-color: ${cssTheme.colors.selectionCard.disabled.background};
  box-shadow: inset 0 0 0 ${BOX_SHADOW_WIDTH}px
      transparent;
  color: ${cssTheme.colors.selectionCard.disabled.content};
  cursor: not-allowed;
  &.--with-border {
    border-color: ${cssTheme.colors.selectionCard.disabled.border};
    box-shadow: inset 0 0 0 ${BOX_SHADOW_WIDTH}px
      ${cssTheme.colors.selectionCard.disabled.border};
  }
`

const PrefixWrapper = styled.div`
  grid-area: prefix;
  margin-inline-end: ${cssTheme.layout.spacing.s300};
  color: ${cssTheme.colors.selectionCard.unselected.content};
`

const TitleWrapper = styled.div`
  grid-area: title;
  color: ${cssTheme.colors.selectionCard.hover.title};
`

const ContentContainer = styled.div`
  ${cssTheme.typography.bodyS}
  grid-area: content;
  color: ${cssTheme.colors.selectionCard.unselected.content};
`

const InnerLabel = styled.label`
  position: relative;
  width: 100%;
  display: block;
  cursor: pointer;
`

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  text-align: start;
  margin: ${BOX_SHADOW_WIDTH}px; /* prevents the box-shadow from being cut off*/

  border-radius: ${cssTheme.layout.radius.md};
  transition-property: background-color, border-color, box-shadow;
  transition-duration: 0.3s;
  transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1);

  border: 1px solid transparent;
  background-color: ${cssTheme.colors.selectionCard.unselected.background};
  color: ${cssTheme.colors.selectionCard.unselected.content};
  &.--with-border {
    border-color: ${cssTheme.colors.selectionCard.unselected.border};
  }

  &.--is-selected {
    ${activeStyles}
  }
  &.--is-disabled,
  &.--is-selected.--is-disabled {
    ${disabledStyles}
  }
  ${linariaMq.desktopXs} {
    &:hover {
      background-color: ${cssTheme.colors.selectionCard.hover.background};
      &.--with-border {
        border-color: ${cssTheme.colors.selectionCard.hover.border};
      }
      &.--is-selected:not(.--is-disabled) {
        ${activeStyles}
      }
      &.--is-disabled {
        ${disabledStyles}
      }
    }
  }

  &:has(input:focus-visible):not(.--is-disabled) {
    outline: 2px solid ${cssTheme.colors.border.interactive.primaryActive};
    outline-offset: 2px;

    /* The card outline is the focus indicator; the inner control must not
       show its own ring on top of it. */
    ${CheckboxIcon},
    ${RadioIcon} {
      outline: none;
    }
  }

  &.--is-disabled {
    ${PrefixWrapper} {
      color: ${cssTheme.colors.selectionCard.disabled.content};
      img {
        opacity: 0.2;
      }
    }
    ${TitleWrapper} {
      color: ${cssTheme.colors.selectionCard.disabled.title};
    }
    ${ContentContainer} {
      color: ${cssTheme.colors.selectionCard.disabled.content};
    }
  }
`

const ControlButtonWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  grid-area: control;
  margin-left: ${cssTheme.layout.spacing.s300};

  &.--hide-default-control-button {
    opacity: 0;
  }
`

const SelectionCardBody = styled.div`
  width: 100%;
  padding: ${cssTheme.layout.spacing.s500};
  padding-right: ${cssTheme.layout.spacing.s400};
  flex-grow: 1;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas:
    'prefix title'
    'prefix content';
  &.--with-control-button {
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
      'prefix title control'
      'prefix content control';
  }
  &.--vertical {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      'prefix control'
      'title control'
      'content control';
  }
`

const LabelText = styled(Tag)`
  position: absolute;
  top: ${minus(cssTheme.layout.spacing.s400)};
  left: ${cssTheme.layout.spacing.s300};
  &.--is-selected {
    background-color: ${cssTheme.colors.selectionCard.selected.tag.background};
    color: ${cssTheme.colors.selectionCard.selected.tag.content};
  }
`

const MotionWrapper = styled(motion.div)`
  width: calc(100% - 2px);
  margin: 1px;
  padding: ${cssTheme.layout.spacing.s600} ${cssTheme.layout.spacing.s500};
  background-color: ${cssTheme.colors.selectionCard.unselected.background};
  border-bottom-left-radius: ${cssTheme.layout.radius.md};
  border-bottom-right-radius: ${cssTheme.layout.radius.md};

  ${linariaMq.desktopXs} {
    padding-inline: ${cssTheme.layout.spacing.s600};
  }
`

export const SelectionCard = ({
  selected,
  dataId = 'SelectionCard',
  name,
  onChange,
  value,
  children,
  labelText,
  withControlButton = true,
  withBorder = true,
  disabled = false,
  className,
  variant = 'radio',
  style,
  prefix,
  title,
  isVertical = false,
  hasCheckmarkError,
  motionWrapperProps,
  expandableContent,
  hideDefaultControlButton = false
}: Props) => {
  return (
    <Wrapper
      className={cx(
        withBorder && '--with-border',
        selected && '--is-selected',
        disabled && '--is-disabled',
        className
      )}
      onClick={withControlButton ? undefined : onChange}
      data-id={dataId}
      style={style}
    >
      <InnerLabel>
        {labelText && (
          <LabelText
            variant={disabled ? 'neutral' : 'brand'}
            className={cx(
              selected && !disabled && '--is-selected',
              '--tag-label-container'
            )}
          >
            <Text variant="labelXS">{labelText}</Text>
          </LabelText>
        )}

        <SelectionCardBody
          className={cx(
            isVertical && `--vertical`,
            withControlButton && `--with-control-button`
          )}
        >
          {Boolean(prefix) && <PrefixWrapper>{prefix}</PrefixWrapper>}
          {Boolean(title) && (
            <TitleWrapper>
              <Text variant="labelS">{title}</Text>
            </TitleWrapper>
          )}
          {Boolean(children) && <ContentContainer>{children}</ContentContainer>}
          {withControlButton && (
            <ControlButtonWrapper
              className={cx(
                !selected &&
                  hideDefaultControlButton &&
                  '--hide-default-control-button'
              )}
            >
              {variant === 'checkbox' && (
                <Checkbox
                  checked={selected}
                  onChange={onChange}
                  disabled={disabled}
                  hasError={hasCheckmarkError}
                  name={name}
                  value={value}
                />
              )}
              {variant === 'radio' && (
                <Radio
                  checked={selected}
                  onChange={onChange}
                  disabled={disabled}
                  name={name}
                  value={value}
                  hasError={hasCheckmarkError}
                />
              )}
            </ControlButtonWrapper>
          )}
        </SelectionCardBody>
      </InnerLabel>
      <AnimatePresence initial={false}>
        {selected && expandableContent && !disabled && (
          <MotionWrapper {...motionWrapperProps}>
            {expandableContent}
          </MotionWrapper>
        )}
      </AnimatePresence>
    </Wrapper>
  )
}
